"use client";

import { useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { buttonClassName } from "@/app/Components/buttonStyles";
import {
  OperationalResult,
  OperationalStatus,
} from "@/app/Components/OperationalPage";
import { resolveEventImageUrl } from "@/app/lib/eventImages";
import type { CampusEvent } from "@/app/types/events";
import { EVENT_SOURCE_LABELS } from "@/app/types/events";
import { formatEventTime } from "../eventPresentation";

interface EventRowProps {
  event: CampusEvent;
}

export default function EventRow({ event }: EventRowProps) {
  const sourceLabel = EVENT_SOURCE_LABELS[event.source] ?? event.source;
  const timeStr = formatEventTime(event.start_time, event.end_time);
  const imageSrc = resolveEventImageUrl(event.image_url);
  const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null);
  const showImage = imageSrc && failedImageSrc !== imageSrc;

  return (
    <OperationalResult
      actions={
        <a
          href={event.source_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View details: ${event.title}`}
          className={buttonClassName({ variant: "secondary" })}
        >
          View details
          <ArrowTopRightOnSquareIcon aria-hidden="true" className="h-4 w-4" />
        </a>
      }
    >
      <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-center">
        {showImage ? (
          <div className="h-24 w-full shrink-0 overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] sm:w-32">
            <img
              src={imageSrc}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setFailedImageSrc(imageSrc)}
            />
          </div>
        ) : null}

        {/* Content */}
        <div className="flex-1 flex flex-col justify-center min-w-0 gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <OperationalStatus>{sourceLabel}</OperationalStatus>
            {event.category && (
              <span className="text-xs font-semibold text-[var(--ss-text-muted)]">
                {event.category}
              </span>
            )}
            {event.is_free && (
              <OperationalStatus tone="positive">Free</OperationalStatus>
            )}
          </div>

          <h3 className="text-base sm:text-lg font-semibold text-[var(--ss-text)] leading-tight break-words">
            {event.title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--ss-text-muted)]">
            {event.host && (
              <span className="font-medium break-words">
                {event.host}
              </span>
            )}
            {event.location && (
              <div className="flex items-center gap-1 min-w-0">
                <MapPinIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span className="break-words">{event.location}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--ss-text)]">
            <ClockIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span>{timeStr}</span>
          </div>
        </div>
      </div>
    </OperationalResult>
  );
}
